// src/firebase/services.js

import {
  collection, doc, getDoc, setDoc, addDoc,
  updateDoc, deleteDoc, query, onSnapshot,
  orderBy, serverTimestamp, writeBatch, getDocs, where, deleteField, limit, startAfter,
  runTransaction,
} from "firebase/firestore";
import { db, auth } from "./config";
import { uploadToCloudinary, deleteFromCloudinary } from "./cloudinary";
import {
  normalizeTransportName, normalizeTransportGst,
  findTransportConflict, getTransportConflictMessage,
} from "../utils/transport";

// ─── BRANDING ─────────────────────────────────────────────────────────────────
export const saveBranding = async (data) => {
  await setDoc(doc(db, "settings", "branding"),
    { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

const defaultBranding = () => ({
  brandName:    "Nilam Synthetics",
  tagline:      "Elegance Woven in Every Thread",
  logoUrl:      null,
  whatsapp:     "",
  phone:        "",
  address:      "Ahmedabad, Gujarat, India",
  ownerGstNumber: "",
  bankName: "",
  bankIfscCode: "",
  bankAccountNumber: "",
  upiId: "boism-9825156797@boi",   // payee VPA for invoice payment QRs
  upiName: "Nilam Synthetics",      // payee name shown in UPI apps
  upiMcc: "",                       // merchant category code (optional)
  primaryColor: "#F47B20",
});

// ─── ONE-TIME FETCHES (for public pages — no real-time) ─────────────────────
export const fetchBranding = async () => {
  const snap = await getDoc(doc(db, "settings", "branding"));
  return snap.exists() ? { ...defaultBranding(), ...snap.data() } : defaultBranding();
};

export const fetchCategories = async () => {
  const q = query(collection(db, "categories"), orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const fetchDesigns = async () => {
  const q = query(collection(db, "designs"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Public pages (catalog search, category sort/filter) need the whole catalogue
// at most once per visit. Reuse one in-flight/finished result for a few
// minutes so repeated searches, sort changes and in-app navigation don't
// re-read every design document each time.
const PUBLIC_DESIGNS_TTL_MS = 10 * 60 * 1000;
let publicDesignsCache = null; // { promise, at }

export const fetchDesignsCached = () => {
  const now = Date.now();
  if (publicDesignsCache && now - publicDesignsCache.at < PUBLIC_DESIGNS_TTL_MS) {
    return publicDesignsCache.promise;
  }
  const promise = fetchDesigns().catch((err) => {
    publicDesignsCache = null; // don't cache failures
    throw err;
  });
  publicDesignsCache = { promise, at: now };
  return promise;
};

export const fetchDesignsPaginated = async (limitCount = 20, lastDoc = null, categorySlug = null) => {
  const constraints = [orderBy("createdAt", "desc")];
  if (categorySlug) {
    constraints.unshift(where("categorySlug", "==", categorySlug));
  }
  constraints.push(limit(limitCount));
  let q = query(collection(db, "designs"), ...constraints);
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  const snap = await getDocs(q);
  return {
    designs: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    hasMore: snap.docs.length === limitCount,
  };
};

export const fetchBanners = async () => {
  const q = query(collection(db, "banners"), orderBy("order", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Normalises a stored default-discount setting into a usable 0-100 percentage.
const normalizeDiscountSetting = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(100, parsed);
};

// Normalises a stored minimum-order-value setting into a usable amount (>= 0).
// 0 means "no minimum" — the auto discount is then always eligible.
const normalizeAmountSetting = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

const defaultQuotationSettings = () => ({
  allowPriceEdit: false,
  allowDiscount: false,
  allowDiscountInvoice: false,
  defaultDiscountQuotation: 0,
  defaultDiscountInvoice: 0,
  minOrderValueQuotation: 0,
  minOrderValueInvoice: 0,
  autoSendWhatsApp: false,
  showFirmName: false,
  showPackagingCharges: false,
  fetchDesignDetails: false,
});

export const fetchQuotationSettings = async () => {
  const snap = await getDoc(doc(db, "settings", "quotation"));
  return snap.exists() ? { ...defaultQuotationSettings(), ...snap.data() } : defaultQuotationSettings();
};

export const subscribeQuotationSettings = (cb) =>
  onSnapshot(doc(db, "settings", "quotation"), snap =>
    cb(snap.exists() ? { ...defaultQuotationSettings(), ...snap.data() } : defaultQuotationSettings())
  );

export const saveQuotationSettings = async (data) => {
  // merge keeps `numberCounters` (see QUOTATION / BILL NUMBER COUNTERS) intact.
  await setDoc(doc(db, "settings", "quotation"), {
    allowPriceEdit: Boolean(data?.allowPriceEdit),
    allowDiscount: Boolean(data?.allowDiscount),
    allowDiscountInvoice: Boolean(data?.allowDiscountInvoice),
    defaultDiscountQuotation: normalizeDiscountSetting(data?.defaultDiscountQuotation),
    defaultDiscountInvoice: normalizeDiscountSetting(data?.defaultDiscountInvoice),
    minOrderValueQuotation: normalizeAmountSetting(data?.minOrderValueQuotation),
    minOrderValueInvoice: normalizeAmountSetting(data?.minOrderValueInvoice),
    autoSendWhatsApp: Boolean(data?.autoSendWhatsApp),
    showFirmName: Boolean(data?.showFirmName),
    showPackagingCharges: Boolean(data?.showPackagingCharges),
    fetchDesignDetails: Boolean(data?.fetchDesignDetails),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ─── ADMIN AUTH ───────────────────────────────────────────────────────────────
export const seedAdminIfNeeded = async () => {
  const snap = await getDoc(doc(db, "settings", "admin"));
  if (!snap.exists()) {
    await setDoc(doc(db, "settings", "admin"), {
      username: "admin", password: "nilam2025",
      mustChangePass: true, createdAt: serverTimestamp(),
    });
  }
};

export const verifyAdmin = async (username, password) => {
  const snap = await getDoc(doc(db, "settings", "admin"));
  if (!snap.exists()) return false;
  const d = snap.data();
  return d.username === username && d.password === password;
};

export const updateAdminCredentials = async (username, password) => {
  await setDoc(doc(db, "settings", "admin"), {
    username, password, mustChangePass: false, updatedAt: serverTimestamp(),
  });
};

// ─── CATEGORIES ───────────────────────────────────────────────────────────────
export const DEFAULT_CATEGORIES = [
  { name: "2 Pcs Set",   slug: "2pcs",     order: 1, icon: "👗", active: true },
  { name: "3 Pcs Set",   slug: "3pcs",     order: 2, icon: "✨", active: true },
  { name: "Kurti",       slug: "kurti",    order: 3, icon: "🌸", active: true },
  { name: "Coord Set",   slug: "coord",    order: 4, icon: "💫", active: true },
  { name: "Frock Style", slug: "frock",    order: 5, icon: "🌺", active: true },
  { name: "Short Top",   slug: "shorttop", order: 6, icon: "🎀", active: true },
  { name: "Others",      slug: "others",   order: 7, icon: "🛍️", active: true },
];

let _seeding = false;
export const seedCategoriesIfNeeded = async () => {
  if (_seeding) return;
  _seeding = true;
  try {
    const snap = await getDoc(doc(db, "settings", "categoriesSeeded"));
    if (snap.exists()) return;
    const batch = writeBatch(db);
    DEFAULT_CATEGORIES.forEach(cat => {
      batch.set(doc(collection(db, "categories")), { ...cat, createdAt: serverTimestamp() });
    });
    batch.set(doc(db, "settings", "categoriesSeeded"), { at: serverTimestamp() });
    await batch.commit();
  } finally {
    _seeding = false;
  }
};

export const subscribeCategories = (cb) => {
  const q = query(collection(db, "categories"), orderBy("order", "asc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const addCategory = async (data, imageFile) => {
  let bannerUrl = null;
  if (imageFile) bannerUrl = await uploadToCloudinary(imageFile, "categories");
  await addDoc(collection(db, "categories"), {
    ...data, bannerUrl, active: true, createdAt: serverTimestamp(),
  });
};

export const updateCategory = async (id, data, imageFile) => {
  let updates = { ...data, updatedAt: serverTimestamp() };
  if (imageFile) updates.bannerUrl = await uploadToCloudinary(imageFile, "categories");
  await updateDoc(doc(db, "categories", id), updates);
};

export const deleteCategory = async (id) => {
  await deleteDoc(doc(db, "categories", id));
};

export const toggleCategoryActive = async (id, active) => {
  await updateDoc(doc(db, "categories", id), { active, updatedAt: serverTimestamp() });
};

// ─── DESIGNS ──────────────────────────────────────────────────────────────────
export const subscribeDesigns = (cb) => {
  const q = query(collection(db, "designs"), orderBy("createdAt", "desc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};
const getDesignPrefix = (categorySlug = "others") =>
  (categorySlug || "others").replace(/[^a-z0-9]/gi, "").toUpperCase().slice(0, 4) || "DSN";

export const generateDesignNoByCategory = async (categorySlug, excludeId = null) => {
  const normalizedCategory = categorySlug || "others";
  const prefix = getDesignPrefix(normalizedCategory);
  const q = query(collection(db, "designs"), where("categorySlug", "==", normalizedCategory));
  const snap = await getDocs(q);

  let max = 0;
  snap.forEach((d) => {
    if (excludeId && d.id === excludeId) return;
    const designNo = d.data()?.designNo || "";
    const m = String(designNo).match(/-(\d+)$/);
    if (!m) return;
    max = Math.max(max, Number(m[1] || 0));
  });

  return `${prefix}-${String(max + 1).padStart(3, "0")}`;
};

// ─── PRODUCT PAGE REFRESH ────────────────────────────────────────────────────
// Product pages (products.nilamsynthetics.com/products/:slug) are cached for an
// hour. After a design changes, ask the products app to purge just those pages
// so the change shows immediately. Fire-and-forget: a failure here never blocks
// or fails the admin save — the page still refreshes on its own within the hour.
const PRODUCTS_API_URL =
  process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";

export const refreshProductPages = ({ ids = [], designNos = [] } = {}) => {
  const cleanIds = ids.filter(Boolean);
  const cleanDesignNos = designNos.filter(Boolean);
  if (!cleanIds.length && !cleanDesignNos.length) return;
  (async () => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) return;
      const res = await fetch(`${PRODUCTS_API_URL}/api/revalidate-products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ ids: cleanIds, designNos: cleanDesignNos }),
      });
      if (!res.ok) console.warn("Product page refresh failed:", res.status);
    } catch (err) {
      console.warn("Product page refresh failed:", err);
    }
  })();
};

// newImageFiles = File[] selected by user
export const addDesign = async (data, newImageFiles = []) => {
  const previousLatestDesignsSnap = await getDocs(
    query(collection(db, "designs"), orderBy("createdAt", "desc"), limit(10))
  );
  const previousTenthDesign = previousLatestDesignsSnap.docs[9];

  const images = newImageFiles.length > 0
    ? await Promise.all(newImageFiles.map(f => uploadToCloudinary(f, "designs")))
    : [];
  const generatedDesignNo = await generateDesignNoByCategory(data.categorySlug || "others");
  const { name, designNo, ...restData } = data;
  const newDesignRef = await addDoc(collection(db, "designs"), {
    ...restData,
    badge: "new",
    designNo: generatedDesignNo,
    images,
    imageUrl: images[0] || null,
    active: true,
    visibleOnWebsite: Boolean(data.visibleOnWebsite),
    is_notified: false,
    createdAt: serverTimestamp(),
  });

  if (previousTenthDesign) {
    await updateDoc(doc(db, "designs", previousTenthDesign.id), {
      badge: "",
      updatedAt: serverTimestamp(),
    });
  }

  refreshProductPages({
    ids: [newDesignRef.id, previousTenthDesign?.id],
    designNos: [generatedDesignNo, previousTenthDesign?.data()?.designNo],
  });
};

// existingImageUrls = string[] of URLs already in Firestore to KEEP
// newImageFiles     = File[] of new images to upload and append
export const updateDesign = async (id, data, existingImageUrls = [], newImageFiles = []) => {
  // Upload new files
  const newUrls = newImageFiles.length > 0
    ? await Promise.all(newImageFiles.map(f => uploadToCloudinary(f, "designs")))
    : [];

  const images = [...existingImageUrls, ...newUrls];

  // Clean data — remove any stray helper fields
  const { existingImages, name, ...cleanData } = data;
  const currentDoc = await getDoc(doc(db, "designs", id));
  const current = currentDoc.exists() ? currentDoc.data() : null;

  const categoryChanged = (current?.categorySlug || "others") !== (cleanData.categorySlug || "others");
  const designNo = categoryChanged
    ? await generateDesignNoByCategory(cleanData.categorySlug || "others", id)
    : (current?.designNo || await generateDesignNoByCategory(cleanData.categorySlug || "others", id));

  await updateDoc(doc(db, "designs", id), {
    ...cleanData,
    designNo,
    name: deleteField(),
    images,
    imageUrl: images[0] || null,
    updatedAt: serverTimestamp(),
  });

  // Purge both the old and the new design-number URLs (category changes
  // regenerate the design number).
  refreshProductPages({ ids: [id], designNos: [current?.designNo, designNo] });
};

// designNo is optional; passing it also refreshes the /products/<design-no> URL.
export const toggleDesignActive = async (id, active, designNo = null) => {
  await updateDoc(doc(db, "designs", id), { active, updatedAt: serverTimestamp() });
  refreshProductPages({ ids: [id], designNos: [designNo] });
};

export const deleteDesign = async (id, images = [], designNo = null) => {
  for (const url of images) await deleteFromCloudinary(url);
  await deleteDoc(doc(db, "designs", id));
  refreshProductPages({ ids: [id], designNos: [designNo] });
};

// ─── BANNERS ──────────────────────────────────────────────────────────────────
export const subscribeBanners = (cb) => {
  const q = query(collection(db, "banners"), orderBy("order", "asc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const addBanner = async (data, imageFile) => {
  let imageUrl = null;
  if (imageFile) imageUrl = await uploadToCloudinary(imageFile, "banners");
  await addDoc(collection(db, "banners"), {
    ...data, imageUrl, active: true, createdAt: serverTimestamp(),
  });
};

export const updateBanner = async (id, data, imageFile) => {
  let updates = { ...data, updatedAt: serverTimestamp() };
  if (imageFile) updates.imageUrl = await uploadToCloudinary(imageFile, "banners");
  await updateDoc(doc(db, "banners", id), updates);
};

export const deleteBanner = async (id, imageUrl) => {
  if (imageUrl) await deleteFromCloudinary(imageUrl);
  await deleteDoc(doc(db, "banners", id));
};

const toTitleCase = (value) =>
  String(value || "").replace(/\b\w/g, char => char.toUpperCase());

// ─── CUSTOMERS ────────────────────────────────────────────────────────────────
export const subscribeCustomers = (cb) => {
  const q = query(collection(db, "customers"), orderBy("created_at", "desc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const fetchCustomers = async () => {
  const q = query(collection(db, "customers"), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addCustomer = async (data) => {
  return addDoc(collection(db, "customers"), {
    customer_name: toTitleCase(data.customer_name || ""),
    address: toTitleCase(data.address || ""),
    transport_name: toTitleCase(data.transport_name || ""),
    transport_id: data.transport_id || "",
    city: toTitleCase(data.city || ""),
    pincode: data.pincode || "",
    mobile_number: data.mobile_number || "",
    firm_name: toTitleCase(data.firm_name || ""),
    agent_name: toTitleCase(data.agent_name || ""),
    gst_number: data.gst_number || "",
    state: data.state || "",
    status: data.status || "active",
    created_at: serverTimestamp(),
    updated_at: null,
    deleted_at: null,
  });
};

export const updateCustomer = async (id, data) => {
  await updateDoc(doc(db, "customers", id), {
    customer_name: toTitleCase(data.customer_name || ""),
    address: toTitleCase(data.address || ""),
    transport_name: toTitleCase(data.transport_name || ""),
    transport_id: data.transport_id || "",
    city: toTitleCase(data.city || ""),
    pincode: data.pincode || "",
    mobile_number: data.mobile_number || "",
    firm_name: toTitleCase(data.firm_name || ""),
    agent_name: toTitleCase(data.agent_name || ""),
    gst_number: data.gst_number || "",
    state: data.state || "",
    status: data.status || "active",
    updated_at: serverTimestamp(),
  });
};

export const deleteCustomer = async (id) => {
  await updateDoc(doc(db, "customers", id), {
    status: "deleted",
    deleted_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
};

// ─── TRANSPORTS ───────────────────────────────────────────────────────────────
// Independent transport registry (name + GST). Customers are assigned a
// transport; the assigned transport's name + GST then flows into the quotation
// as its single `transport_name` field.
export const subscribeTransports = (cb) => {
  const q = query(collection(db, "transports"), orderBy("name", "asc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const fetchTransports = async () => {
  const q = query(collection(db, "transports"), orderBy("name", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Look up an active transport by name + GST (code-level uniqueness helper).
 * Returns the conflicting transport or `null`.
 */
export const findTransportByNameAndGst = async (name, gst, excludeId = null) => {
  const transports = await fetchTransports();
  return findTransportConflict(transports, { name, gst }, excludeId);
};

export const addTransport = async (data) => {
  const name = normalizeTransportName(data.name);
  const gst = normalizeTransportGst(data.gst);

  // Code-level validation (not database level):
  //   1. unique (name + GST) combination, and
  //   2. unique name when GST is empty.
  const conflict = await findTransportByNameAndGst(name, gst);
  if (conflict) throw new Error(getTransportConflictMessage(conflict, gst));

  return addDoc(collection(db, "transports"), {
    name,
    gst,
    status: data.status || "active",
    created_at: serverTimestamp(),
    updated_at: null,
    deleted_at: null,
  });
};

export const updateTransport = async (id, data) => {
  const name = normalizeTransportName(data.name);
  const gst = normalizeTransportGst(data.gst);

  const conflict = await findTransportByNameAndGst(name, gst, id);
  if (conflict) throw new Error(getTransportConflictMessage(conflict, gst));

  await updateDoc(doc(db, "transports", id), {
    name,
    gst,
    status: data.status || "active",
    updated_at: serverTimestamp(),
  });
};

export const deleteTransport = async (id) => {
  await updateDoc(doc(db, "transports", id), {
    status: "deleted",
    deleted_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
};

// ─── QUOTATIONS ───────────────────────────────────────────────────────────────
export const fetchQuotations = async () => {
  const q = query(collection(db, "quotations"), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Fetch ALL quotations belonging to a single customer, newest first.
 *
 * The customer detail view (All Customers → Customer Overview) needs the
 * customer's complete quotation history regardless of the date filter that is
 * active on the All Quotations tab, so we query Firestore directly by
 * `customer_id`. Requires a composite index on `customer_id` (ASC) +
 * `created_at` (DESC) — see firestore.indexes.json.
 */
export const fetchQuotationsForCustomer = async (customerId) => {
  if (!customerId) return [];
  const q = query(
    collection(db, "quotations"),
    where("customer_id", "==", customerId),
    orderBy("created_at", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Fetch only the quotations that fall inside a date range, newest first.
 *
 * The quotation tab defaults to "today", so instead of subscribing to (and
 * re-reading) the entire multi-month quotations collection we only pull the
 * documents for the selected day/range. Requires no extra composite index
 * because it is a single-field range query on `created_at`.
 */
export const fetchQuotationsForDateRange = async ({ from, to }) => {
  const constraints = [orderBy("created_at", "desc")];
  if (from) constraints.push(where("created_at", ">=", from));
  if (to) constraints.push(where("created_at", "<=", to));
  const q = query(collection(db, "quotations"), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Lightweight list of quotation/bill numbers across ALL time. Used purely to
 * auto-generate the next quotation & bill numbers. The visible quotation list is
 * date-bounded (see fetchQuotationsForDateRange), but numbering must still see
 * every document, so we fetch only the tiny number fields here and cache it for
 * the session.
 */
export const fetchQuotationNumbers = async () => {
  const snap = await getDocs(collection(db, "quotations"));
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      quotation_number: data.quotation_number,
      bill_number: data.bill_number,
      created_at: data.created_at,
    };
  });
};

// ─── QUOTATION / BILL NUMBER COUNTERS ────────────────────────────────────────
// The next quotation number is (highest quotation number ever) + 1, and the next
// bill number is (highest bill number in the current financial year) + 1. That
// used to require reading EVERY quotation document on each admin load. Instead
// the maxima are kept in `settings/quotation.numberCounters` (a doc the admin
// already listens to), bumped on each save and rebuilt by a one-off full scan
// only when missing or after a delete.
//
//   numberCounters: { quotationMax: 123, billMaxByFy: { "2026": 45 } }
//   ("2026" = financial year starting 1 April 2026)

const toJsDate = (value) => {
  if (!value) return null;
  const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const financialYearStartOf = (date) =>
  (date.getMonth() + 1 >= 4 ? date.getFullYear() : date.getFullYear() - 1);

// Same parsing rules as the quotation builder used on the full list.
export const computeQuotationCounters = (list = []) => {
  let quotationMax = 0;
  const billMaxByFy = {};
  for (const q of list) {
    const quotationValue = Number(String(q?.quotation_number || "").trim());
    if (Number.isFinite(quotationValue)) {
      quotationMax = Math.max(quotationMax, Math.trunc(quotationValue));
    }

    const rawBill = String(q?.bill_number || "").trim();
    const billMatch = rawBill.match(/\d+/);
    const createdAt = toJsDate(q?.created_at);
    if (billMatch && createdAt) {
      const billValue = Number(billMatch[0]);
      if (Number.isFinite(billValue)) {
        const fy = String(financialYearStartOf(createdAt));
        billMaxByFy[fy] = Math.max(billMaxByFy[fy] || 0, billValue);
      }
    }
  }
  return { quotationMax, billMaxByFy };
};

export const mergeQuotationCounters = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  const billMaxByFy = { ...(a.billMaxByFy || {}) };
  for (const [fy, max] of Object.entries(b.billMaxByFy || {})) {
    billMaxByFy[fy] = Math.max(billMaxByFy[fy] || 0, Number(max) || 0);
  }
  return {
    quotationMax: Math.max(Number(a.quotationMax) || 0, Number(b.quotationMax) || 0),
    billMaxByFy,
  };
};

/** Full scan (one read per quotation) — only when counters are missing or after a delete. */
export const rebuildQuotationCounters = async () => {
  const counters = computeQuotationCounters(await fetchQuotationNumbers());
  try {
    await setDoc(doc(db, "settings", "quotation"),
      { numberCounters: counters }, { merge: true });
  } catch (e) {
    // Roles that can read but not write settings still get correct numbers locally.
    console.warn("Could not store quotation counters:", e);
  }
  return counters;
};

/** After a create/update: raise the stored maxima if this quotation exceeds them (1 read + 1 write). */
export const bumpQuotationCounters = async (quotation) => {
  const ref = doc(db, "settings", "quotation");
  const additions = computeQuotationCounters([quotation]);
  const stored = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? snap.data()?.numberCounters : null;
    if (!current) return null;
    const next = mergeQuotationCounters(current, additions);
    tx.set(ref, { numberCounters: next }, { merge: true });
    return next;
  });
  return stored || rebuildQuotationCounters();
};

export const addQuotation = async (data) => {
  const normalizedQuotationNumber = String(data.quotation_number || "").trim();
  if (!normalizedQuotationNumber) {
    throw new Error("Quotation number is required");
  }

  const quotationYear = Number(data.quotation_year || new Date().getFullYear());
  const duplicateQuery = query(
    collection(db, "quotations"),
    where("quotation_year", "==", quotationYear),
    where("quotation_number", "==", normalizedQuotationNumber)
  );
  const duplicateSnap = await getDocs(duplicateQuery);
  if (!duplicateSnap.empty) {
    throw new Error(`Quotation number "${normalizedQuotationNumber}" already exists for ${quotationYear}`);
  }

  await addDoc(collection(db, "quotations"), {
    quotation_number: normalizedQuotationNumber,
    bill_number: String(data.bill_number || "").trim(),
    quotation_year: quotationYear,
    customer_id: data.customer_id || "",
    customer_name: data.customer_name || "",
    customer_mobile: data.customer_mobile || "",
    customer_city: data.customer_city || "",
    customer_state: data.customer_state || "",
    customer_pincode: data.customer_pincode || "",
    customer_gst: data.customer_gst || "",
    transport_name: data.transport_name || "",
    transport_id: data.transport_id || "",
    items: data.items || [],
    subtotal_amount: Number(data.subtotal_amount || 0),
    discount_amount: Number(data.discount_amount || data.discount || 0),
    discount_percentage: Number(data.discount_percentage || 0),
    gross_amount: Number(data.gross_amount || 0),
    cgst_amount: Number(data.cgst_amount || 0),
    sgst_amount: Number(data.sgst_amount || 0),
    igst_amount: Number(data.igst_amount || 0),
    total_tax_amount: Number(data.total_tax_amount || 0),
    packaging_charges: Number(data.packaging_charges || 0),
    transport_charges: Number(data.transport_charges || 0),
    round_off_amount: Number(data.round_off_amount || 0),
    grand_total: Number(data.grand_total || 0),
    status: data.status || "draft",
    created_at: data.created_at || serverTimestamp(),
    updated_at: null,
  });
};

export const updateQuotation = async (id, data) => {
  const normalizedQuotationNumber = String(data.quotation_number || "").trim();
  if (!normalizedQuotationNumber) {
    throw new Error("Quotation number is required");
  }

  const quotationYear = Number(data.quotation_year || new Date().getFullYear());
  const duplicateQuery = query(
    collection(db, "quotations"),
    where("quotation_year", "==", quotationYear),
    where("quotation_number", "==", normalizedQuotationNumber)
  );
  const duplicateSnap = await getDocs(duplicateQuery);
  const hasDuplicate = duplicateSnap.docs.some(d => d.id !== id);
  if (hasDuplicate) {
    throw new Error(`Quotation number "${normalizedQuotationNumber}" already exists for ${quotationYear}`);
  }

  await updateDoc(doc(db, "quotations", id), {
    quotation_number: normalizedQuotationNumber,
    bill_number: String(data.bill_number || "").trim(),
    quotation_year: quotationYear,
    customer_id: data.customer_id || "",
    customer_name: data.customer_name || "",
    customer_mobile: data.customer_mobile || "",
    customer_city: data.customer_city || "",
    customer_state: data.customer_state || "",
    customer_pincode: data.customer_pincode || "",
    customer_gst: data.customer_gst || "",
    transport_name: data.transport_name || "",
    transport_id: data.transport_id || "",
    items: data.items || [],
    subtotal_amount: Number(data.subtotal_amount || 0),
    discount_amount: Number(data.discount_amount || data.discount || 0),
    discount_percentage: Number(data.discount_percentage || 0),
    gross_amount: Number(data.gross_amount || 0),
    cgst_amount: Number(data.cgst_amount || 0),
    sgst_amount: Number(data.sgst_amount || 0),
    igst_amount: Number(data.igst_amount || 0),
    total_tax_amount: Number(data.total_tax_amount || 0),
    packaging_charges: Number(data.packaging_charges || 0),
    transport_charges: Number(data.transport_charges || 0),
    round_off_amount: Number(data.round_off_amount || 0),
    grand_total: Number(data.grand_total || 0),
    status: data.status || "draft",
    created_at: data.created_at || serverTimestamp(),
    updated_at: serverTimestamp(),
  });
};

export const deleteQuotation = async (id) => {
  await deleteDoc(doc(db, "quotations", id));
};

// ─── NOTIFICATION TEMPLATES ──────────────────────────────────────────────────
export const subscribeNotificationTemplates = (cb) => {
  const q = query(collection(db, "notificationTemplates"), orderBy("updatedAt", "desc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const fetchNotificationTemplates = async () => {
  const q = query(collection(db, "notificationTemplates"), orderBy("updatedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addNotificationTemplate = async (data) => {
  await addDoc(collection(db, "notificationTemplates"), {
    title: data.title,
    body: data.body,
    updatedAt: serverTimestamp(),
  });
};

export const updateNotificationTemplate = async (id, data) => {
  await updateDoc(doc(db, "notificationTemplates", id), {
    title: data.title,
    body: data.body,
    updatedAt: serverTimestamp(),
  });
};

export const deleteNotificationTemplate = async (id) => {
  await deleteDoc(doc(db, "notificationTemplates", id));
};

// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────
export const uploadLogoFile = (file) => uploadToCloudinary(file, "logos");